import { call, put, select, take, takeLatest } from "redux-saga/effects";
import { push } from "react-router-redux";
import moment from "moment";

import { types, selectors as currencyExchangeSelectors } from "reducers/currencyExchange";
import { actions as formActions } from "reducers/form";
import { actions as notificationActions } from "reducers/notification";
import { selectors as accountsSelectors } from "reducers/accounts";
import { getAccountDescription } from "util/accounts";
import { resolveTipoCuentaFromAccounts } from "util/currencyExchangeFraudPayload";
import * as currencyExchangeMiddleware from "middleware/currencyExchange";
import * as session from "middleware/session";
import * as utilDownload from "util/download";
import * as i18n from "util/i18n";

function* loadQuote() {
    const response = yield call(currencyExchangeMiddleware.getQuote);

    if (response.type === "W") {
        yield put({
            type: types.QUOTE_FAILURE,
            error: response.data?.message,
        });
        return;
    }

    const data = response?.data?.data || response?.data || {};
    const { cotizacionCompra, cotizacionVenta } = data;

    yield put({
        type: types.QUOTE_SUCCESS,
        cotizacionCompra: cotizacionCompra || null,
        cotizacionVenta: cotizacionVenta || null,
    });
}

function* loadHistory({ filterType, pageNumber, isInfiniteScroll = false }) {
    try {
        const response = yield call(currencyExchangeMiddleware.getHistory, filterType, pageNumber);

        if (response.type === "W") {
            yield put({
                type: types.HISTORY_FAILURE,
                error: response.data?.message,
            });
            return;
        }

        const data = response?.data?.data || {};
        const { transactions, totalRows, totalPages, pageNumber: currentPage } = data;

        yield put({
            type: types.HISTORY_SUCCESS,
            transactions: transactions || [],
            totalCount: totalRows || 0,
            totalPages: totalPages || 0,
            pageNumber: currentPage || 1,
            isInfiniteScroll,
        });
    } catch (error) {
        yield put({
            type: types.HISTORY_FAILURE,
            error: error?.message,
        });
    }
}

function* createPreview({ data }) {
    try {
        const requestData = {
            ...data,
            importeDebito: String(data.importeDebito),
        };
        const response = yield call(currencyExchangeMiddleware.createRequest, requestData);

        if (response.type === "W") {
            yield put({
                type: types.PREVIEW_FAILURE,
                error: response.data?.message,
            });
            yield put(
                notificationActions.showNotification(
                    response.data?.message || "currencyExchange.error.preview",
                    "error",
                    ["currencyExchange"],
                ),
            );
            return;
        }

        const responseData = response?.data?.data || response?.data || {};

        yield put({
            type: types.PREVIEW_SUCCESS,
            previewData: {
                solicitudCambioId: responseData.solicitudCambioId,
                numeroOperacion: responseData.numeroOperacion,
                tipoOperacion: data.tipoOperacion,
                cotizacion: responseData.cotizacion,
                importeDebito: responseData.importeDebito,
                importeCredito: responseData.importeCredito,
                estado: responseData.estado,
                cuentaDebito: data.cuentaDebito,
                cuentaCredito: data.cuentaCredito,
                tipoCuentaDebito: data.tipoCuentaDebito,
                tipoCuentaCredito: data.tipoCuentaCredito,
            },
        });

        yield put(push("/v2/investment/currencyExchange/confirm"));
    } catch (error) {
        const rawMessage = error?.response?.data?.data?.message || error?.data?.data?.message;
        const errorMessage = rawMessage || i18n.get("currencyExchange.error.preview");
        yield put({
            type: types.PREVIEW_FAILURE,
            error: errorMessage,
        });
        yield put(notificationActions.showNotification(errorMessage, "error", ["currencyExchange"]));
    }
}

function* readTransaction({ transactionId }) {
    try {
        const response = yield call(currencyExchangeMiddleware.readTransaction, transactionId);

        if (response.type === "W") {
            yield put({
                type: types.READ_TRANSACTION_FAILURE,
                error: response.data?.message,
            });
            yield put(
                notificationActions.showNotification(
                    response.data?.message || "currencyExchange.error.readTransaction",
                    "error",
                    ["currencyExchange"],
                ),
            );
            return;
        }

        const transaction = response?.data?.data?.transaction || {};
        const data = transaction.data || {};
        const isBuy = transaction.idActivity === "currencyExchange.confirm.buy";

        const accounts = yield select(accountsSelectors.getAccounts);
        const getFormattedAccount = (accountNumber) => {
            const account = accounts.find((acc) => acc.number === accountNumber);
            if (!account) {
                return accountNumber || "";
            }
            return getAccountDescription(account);
        };

        const rawDateTime =
            transaction.creationDateTime?.toString?.()?.replace("Z", "") ||
            data.dateTime ||
            transaction.submitDateTime ||
            "";
        const dateTime = rawDateTime ? moment(rawDateTime).format("DD/MM/YYYY HH:mm:ss") : "";

        const creatorFullName =
            transaction.creatorFullName ||
            (transaction.userCreatorLastName && transaction.userCreatorFirstName
                ? `${transaction.userCreatorLastName}, ${transaction.userCreatorFirstName}`
                : "");

        const preparedBy = creatorFullName ? { name: creatorFullName, dateTime } : undefined;

        const firstSigner = transaction.signatures?.[0];
        const signedBy = firstSigner
            ? {
                  name: firstSigner.fullName || firstSigner.userFullName || "",
                  dateTime: firstSigner.signatureDate
                      ? moment(firstSigner.signatureDate).format("DD/MM/YYYY HH:mm:ss")
                      : dateTime,
              }
            : undefined;

        const transactionTicket = {
            id: transaction.idTransaction,
            type: isBuy ? "buy" : "sell",
            dateTime,
            voucherNumber: transaction.idTransaction?.substring(0, 8).toUpperCase() || "",
            operationNumber: data.solicitudCambioId?.toString() || "",
            debitAccount: getFormattedAccount(data.cuentaDebito) || data.cuentaDebito || "",
            creditAccount: getFormattedAccount(data.cuentaCredito) || data.cuentaCredito || "",
            debitAmount: data.importeDebito || 0,
            debitCurrency: isBuy ? "ARS" : "USD",
            creditAmount: data.importeCredito || 0,
            creditCurrency: isBuy ? "USD" : "ARS",
            exchangeRate: data.cotizacion || 0,
            preparedBy,
            signedBy,
        };

        yield put({
            type: types.READ_TRANSACTION_SUCCESS,
            transactionTicket,
        });
    } catch (error) {
        yield put({
            type: types.READ_TRANSACTION_FAILURE,
            error: error?.message,
        });
    }
}

function* handleDownloadDeclaration({ format, i18nVar }) {
    yield put({ type: types.FETCHING_DOWNLOAD_START });
    try {
        const { type, data } = yield call(session.exportTermsAndConditionsI18nVar, format, i18nVar);

        if (type === "W") {
            yield put(
                notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["currencyExchange"]),
            );
        } else {
            const { content, fileName } = data.data;
            utilDownload.downloadPdf(fileName, content);
            yield put(
                notificationActions.showNotification(i18n.get("global.download.success"), "success", [
                    "currencyExchange",
                ]),
            );
        }
    } finally {
        yield put({ type: types.FETCHING_DOWNLOAD_END });
    }
}

function* confirmExchange({ solicitudCambioId, tipoOperacion, declaracionJuradaAceptada, otp }) {
    try {
        const previewData = yield select(currencyExchangeSelectors.getPreviewData);
        const accounts = yield select(accountsSelectors.getAccounts);
        const tipoCuentaDebito = resolveTipoCuentaFromAccounts(
            previewData?.cuentaDebito,
            previewData?.tipoCuentaDebito,
            accounts,
        );
        const tipoCuentaCredito = resolveTipoCuentaFromAccounts(
            previewData?.cuentaCredito,
            previewData?.tipoCuentaCredito,
            accounts,
        );
        const confirmFn =
            tipoOperacion === "COMPRA"
                ? currencyExchangeMiddleware.confirmBuyRequest
                : currencyExchangeMiddleware.confirmSellRequest;

        const response = yield call(confirmFn, {
            solicitudCambioId,
            declaracionJuradaAceptada,
            cotizacion: String(previewData.cotizacion),
            importeDebito: String(previewData.importeDebito),
            importeCredito: String(previewData.importeCredito),
            cuentaDebito: previewData?.cuentaDebito,
            cuentaCredito: previewData?.cuentaCredito,
            tipoCuentaDebito,
            tipoCuentaCredito,
            otp,
        });

        if (response.type === "W") {
            yield put({
                type: types.CONFIRM_FAILURE,
                error: response.data?.message,
            });

            if (response?.data?.data?.errorDescription !== null) {
                yield put(
                    notificationActions.showNotification(response?.data?.data?.errorDescription, "error", [
                        "currencyExchangeConfirmation",
                    ]),
                );
            }

            if (response.data?.code !== "API524W") {
                const idTransaction = response?.data?.data?.idTransaction || response?.data?.idTransaction;
                if (idTransaction) {
                    yield put(
                        formActions.readTransaction({
                            pathname: `/transaction/${idTransaction}`,
                            newTransaction: true,
                        }),
                    );
                    yield take("READ_TRANSACTION_REQUEST");
                }
            }
            return;
        }

        const responseData = response?.data?.data || response?.data || {};

        yield put({
            type: types.CONFIRM_SUCCESS,
            confirmData: {
                estado: responseData.estado,
                solicitudCambioId,
                tipoOperacion,
                dateTime: responseData.dateTime,
                idTransaction: responseData.idTransaction,
            },
        });

        yield put(
            formActions.readTransaction({
                pathname: `/transaction/${responseData.idTransaction}`,
                newTransaction: true,
            }),
        );

        yield take("READ_TRANSACTION_REQUEST");
    } catch (error) {
        yield put({
            type: types.CONFIRM_FAILURE,
            error: error?.message,
        });
        yield put(notificationActions.showNotification(i18n.get("token.otp.invalid"), "error", ["currencyExchange"]));
        const errorResponse = error?.response || error;
        const isTokenError = errorResponse?.data?.code === "API524W";
        const idTransaction = errorResponse?.data?.data?.idTransaction || errorResponse?.data?.idTransaction;
        if (!isTokenError && idTransaction) {
            yield put(
                formActions.readTransaction({
                    pathname: `/transaction/${idTransaction}`,
                    newTransaction: true,
                }),
            );
            yield take("READ_TRANSACTION_REQUEST");
        }
    }
}

const sagas = [
    takeLatest(types.QUOTE_REQUEST, loadQuote),
    takeLatest(types.HISTORY_REQUEST, loadHistory),
    takeLatest(types.PREVIEW_REQUEST, createPreview),
    takeLatest(types.DOWNLOAD_DECLARATION_REQUEST, handleDownloadDeclaration),
    takeLatest(types.CONFIRM_REQUEST, confirmExchange),
    takeLatest(types.READ_TRANSACTION_REQUEST, readTransaction),
];

export default sagas;
